/**
 * The left input panel.
 *
 * The backend remains calculation authority. This panel only edits contract
 * inputs and displays backend-supplied engineering data.
 */

import { useState } from 'react';

import {
  CheckboxField, Field, FieldRow, InfoButton, Panel, SectionCard, SelectField, TabBar,
  type TabDefinition,
} from '../shared/components';
import { DEVELOPMENT_DATASET_ID } from '../app/defaultProject';
import { CatalogueSelection, type CatalogueSelectionState } from './catalogue/CatalogueSelection';
import { ZoneLayoutEditor } from './fixing-layout/ZoneLayoutEditor';
import { NationalWindZoneSelector } from './location/NationalWindZoneSelector';
import { SectionProfileInputs } from './section-editor/ProfessionalSectionEditor';
import './multi-roof/MultiRoofWorkspace.css';
import { clampDrawingLabelScale, type DrawingDisplaySettings } from './view/drawingDisplaySettings';
import type { DesignMethod, NationalDataset, NearbyBuilding, Project, RoofModule } from '../gen/ejot/roofing/v1/types';

const TABS: readonly TabDefinition[] = [
  { id: 'general', label: 'General' },
  { id: 'module', label: 'Roof Module' },
  { id: 'products', label: 'Products' },
  { id: 'layout', label: 'Fixing Layout' },
  { id: 'view', label: 'View' },
];

const TERRAIN_OPTIONS = [
  { value: 'terrain-0', label: 'Category 0' },
  { value: 'terrain-I', label: 'Category I' },
  { value: 'terrain-II', label: 'Category II' },
  { value: 'terrain-III', label: 'Category III' },
  { value: 'terrain-IV', label: 'Category IV' },
];

interface InputPanelProps {
  project: Project;
  activeModule: RoofModule | undefined;
  activeModuleId: string;
  designMethods: readonly DesignMethod[];
  nationalDatasets: readonly NationalDataset[];
  layers: LayerVisibility;
  drawingDisplaySettings: DrawingDisplaySettings;
  catalogue: CatalogueSelectionState;
  onProjectChange: (project: Project) => void;
  onModuleChange: (module: RoofModule) => void;
  onActiveModuleChange: (moduleId: string) => void;
  onAddModule: () => void;
  onDeleteModule: (moduleId: string) => void;
  onEditGeometry: () => void;
  onLayerToggle: (layerId: string, visible: boolean) => void;
  onDrawingDisplaySettingsChange: (settings: DrawingDisplaySettings) => void;
  onCatalogueChange: (selection: CatalogueSelectionState) => void;
}

export interface LayerVisibility {
  [layerId: string]: boolean;
}

interface ViewLayerDefinition {
  id: string;
  label: string;
}

export const VIEW_LAYER_GROUPS: readonly { title: string; layers: readonly ViewLayerDefinition[] }[] = [
  {
    title: 'Roof',
    layers: [
      { id: 'roof-solid', label: 'Roof solid' },
      { id: 'roof-outline', label: 'Roof outline' },
      { id: 'roof-slopes', label: 'Roof slopes' },
      { id: 'openings', label: 'Openings' },
    ],
  },
  {
    title: 'Wind',
    layers: [
      { id: 'wind-zones', label: 'Wind zones' },
      { id: 'zone-labels', label: 'Zone labels' },
      { id: 'zone-dimensions', label: 'Zone dimensions' },
      { id: 'overall-dimensions', label: 'Overall dimensions' },
    ],
  },
  {
    title: 'Membrane',
    layers: [
      { id: 'membrane-sheets', label: 'Membrane sheets / courses' },
      { id: 'membrane-overlaps', label: 'Side overlaps' },
      { id: 'membrane-end-laps', label: 'End laps' },
      { id: 'membrane-seam-lines', label: 'Seam lines' },
      { id: 'membrane-course-tags', label: 'Course / roll tags' },
    ],
  },
  {
    title: 'Fixings',
    layers: [
      { id: 'membrane-strips', label: 'Overlap fixing rows' },
      { id: 'fixing-points', label: 'Fixing points' },
      { id: 'fixing-spacing-tags', label: 'Fixing spacing labels' },
      { id: 'perimeter-fixings', label: 'Perimeter fixings' },
    ],
  },
  {
    title: 'Drawing',
    layers: [
      { id: 'construction-lines', label: 'Construction lines' },
      { id: 'north-arrow', label: 'North arrow' },
      { id: 'axes', label: 'Axes' },
      { id: 'grid', label: 'Grid' },
      { id: 'warnings', label: 'Warnings' },
    ],
  },
];

export const VIEW_LAYERS: readonly ViewLayerDefinition[] = VIEW_LAYER_GROUPS.flatMap((group) => group.layers);

export function InputPanel({
  project, activeModule, activeModuleId, designMethods, nationalDatasets, layers, drawingDisplaySettings, catalogue,
  onProjectChange, onModuleChange, onActiveModuleChange, onAddModule, onDeleteModule,
  onEditGeometry, onLayerToggle, onDrawingDisplaySettingsChange, onCatalogueChange,
}: InputPanelProps) {
  const [tab, setTab] = useState('general');
  const metadata = project.metadata ?? {};
  const location = project.location ?? {};

  const updateMetadata = (patch: Partial<typeof metadata>) => {
    onProjectChange({ ...project, metadata: { ...metadata, ...patch } });
  };
  const updateLocation = (patch: Partial<typeof location>) => {
    onProjectChange({ ...project, location: { ...location, ...patch } });
  };

  const manualSelectionActive = location.selection_method === 'LOCATION_SELECTION_METHOD_MANUAL';
  const windSelection = {
    countryCode: location.country_code ?? '',
    datasetId: manualSelectionActive ? '' : location.national_dataset_id ?? '',
    zoneId: manualSelectionActive ? '' : location.wind_zone_id ?? '',
    userDefined: manualSelectionActive && (location.country_code ?? '') !== '',
  };
  const modules = project.modules ?? [];
  const deleteDisabled = modules.length <= 1;

  return (
    <Panel title="Input">
      <div className="input-tab-bar">
        <TabBar tabs={TABS} active={tab} onSelect={setTab} ariaLabel="Input sections" />
      </div>

      <div className="panel-content input-panel-content" id={`panel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`}>
        {tab === 'general' && (
          <>
            <SectionCard title="Project">
              <Field label="Project name" value={metadata.project_name ?? ''}
                onChange={(v) => updateMetadata({ project_name: v })} />
              <Field label="Subject" value={metadata.subject ?? ''}
                onChange={(v) => updateMetadata({ subject: v })} />
            </SectionCard>

            <SectionCard title="Design method">
              <SelectField
                label="Design method"
                value={project.design_method_id ?? ''}
                options={designMethods.map((method) => ({
                  value: method.id ?? '',
                  label: compactDesignMethodLabel(method),
                }))}
                placeholder="No design method available"
                onChange={designMethods.length > 0
                  ? (v) => onProjectChange({ ...project, design_method_id: v })
                  : undefined}
              />
              {designMethods.length === 0 && (
                <p className="field-note field-note-blocking">
                  No engineering method is enabled in this deployment. Production remains fail-closed.
                </p>
              )}
            </SectionCard>

            <SectionCard title="Location and wind">
              <InfoButton label="Information about source-backed wind selection">
                Select a country and then choose one of its source-backed base wind speeds. Each speed retains its
                national dataset and wind-zone identifiers. User Defined is available for every listed country and
                remains a separate manual input path.
              </InfoButton>
              <NationalWindZoneSelector
                datasets={nationalDatasets}
                selection={windSelection}
                userDefinedValue={manualSelectionActive
                  ? location.base_wind_velocity_override?.value
                  : undefined}
                onChange={(selection) => {
                  if (selection.userDefined) {
                    updateLocation({
                      country_code: selection.countryCode,
                      national_dataset_id: DEVELOPMENT_DATASET_ID,
                      wind_zone_id: '',
                      selection_method: 'LOCATION_SELECTION_METHOD_MANUAL',
                      base_wind_velocity_override: undefined,
                      manual_wind_velocity: undefined,
                    });
                    return;
                  }
                  updateLocation({
                    country_code: selection.countryCode,
                    national_dataset_id: selection.datasetId,
                    wind_zone_id: selection.zoneId,
                    selection_method: 'LOCATION_SELECTION_METHOD_LIST',
                    base_wind_velocity_override: undefined,
                    manual_wind_velocity: undefined,
                  });
                }}
                onUserDefinedValueChange={(v) => {
                  const value = finiteNumber(v);
                  const countryCode = windSelection.countryCode;
                  updateLocation({
                    country_code: countryCode,
                    national_dataset_id: DEVELOPMENT_DATASET_ID,
                    wind_zone_id: '',
                    selection_method: 'LOCATION_SELECTION_METHOD_MANUAL',
                    base_wind_velocity_override: manualWindQuantity(value),
                    manual_wind_velocity: {
                      value: manualWindQuantity(value),
                      country_code: countryCode,
                      source_citation: `User Defined base wind speed for ${countryCode || 'unspecified country'}; project input, not a National Annex zone value.`,
                      source_reference: {
                        reference_id: `USER-DEFINED-WIND:${countryCode || 'UNSPECIFIED'}`,
                        display_text: 'User Defined project wind input',
                        source_version: '1',
                      },
                      national_annex: 'User Defined — no National Annex zone value inferred',
                    },
                  });
                }}
              />
              <Field label="Elevation above sea level" unit="m" type="number"
                value={millimetresToMetres(location.elevation_above_sea_level?.millimetres)}
                onChange={(v) => updateLocation({
                  elevation_above_sea_level: { millimetres: metresToMillimetres(v), origin: 'VALUE_ORIGIN_INPUT' },
                })} />
            </SectionCard>

            <SectionCard title="Terrain and exposure">
              <SelectField
                label="Terrain category"
                value={location.terrain_category_id ?? ''}
                options={TERRAIN_OPTIONS}
                onChange={(v) => updateLocation({ terrain_category_id: v })}
              />
              <FieldRow>
                <Field label="Directional factor" symbol="c_dir" type="number"
                  value={String(location.directional_factor?.value ?? '')}
                  onChange={(v) => updateLocation({ directional_factor: quantityInput(v, 'c_dir') })} />
                <Field label="Seasonal factor" symbol="c_season" type="number"
                  value={String(location.seasonal_factor?.value ?? '')}
                  onChange={(v) => updateLocation({ seasonal_factor: quantityInput(v, 'c_season') })} />
              </FieldRow>
              <Field label="Air density" symbol="rho" unit="kg/m3" type="number"
                value={String(location.air_density?.value ?? '')}
                onChange={(v) => updateLocation({
                  air_density: { ...quantityInput(v, 'rho'), unit: 'kg/m3', display_precision: 3 },
                })} />
            </SectionCard>

            <SectionCard title="Nearby buildings" defaultOpen={false}>
              <CheckboxField label="Consider nearby buildings"
                checked={location.nearby_buildings_enabled ?? false}
                onChange={(checked) => updateLocation({
                  nearby_buildings_enabled: checked,
                  nearby_buildings: checked
                    ? ((location.nearby_buildings ?? []).length > 0 ? location.nearby_buildings : [createNearbyBuilding(1)])
                    : [],
                })} />
              {location.nearby_buildings_enabled && (
                <NearbyBuildingsEditor
                  buildings={location.nearby_buildings ?? []}
                  onChange={(nearby_buildings) => updateLocation({ nearby_buildings })}
                />
              )}
              <p className="field-note field-note-blocking">
                Nearby-building geometry is carried through the project/backend contract. No aerodynamic pressure correction is invented: calculations remain fail-closed until an approved source-backed nearby-building rule is available.
              </p>
            </SectionCard>
          </>
        )}

        {tab === 'module' && activeModule && (
          <>
            <div className="roof-module-tabs" role="tablist" aria-label="Roof modules">
              {modules.map((module, index) => {
                const id = module.id ?? `roof-${index + 1}`;
                const label = module.display_name?.trim() || `Roof ${index + 1}`;
                const selected = id === (activeModuleId || activeModule.id || '');
                return (
                  <div key={id} className={selected ? 'roof-module-chip is-active' : 'roof-module-chip'}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      className="roof-module-select"
                      onClick={() => onActiveModuleChange(id)}
                    >
                      {label}
                    </button>
                    <button
                      type="button"
                      className="roof-module-delete"
                      disabled={deleteDisabled}
                      aria-label={deleteDisabled ? `${label} cannot be deleted because at least one roof is required` : `Delete ${label}`}
                      title={deleteDisabled ? 'At least one roof must remain in the project.' : `Delete ${label}`}
                      onClick={() => onDeleteModule(id)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              <button type="button" className="text-button roof-module-add" onClick={onAddModule} aria-label="Add roof module">
                + Add roof
              </button>
            </div>

            <SectionCard title="Basic parameters">
              <FieldRow>
                <Field label="Module name" value={activeModule.display_name ?? ''}
                  onChange={(v) => onModuleChange({ ...activeModule, display_name: v })} />
                <Field label="Quantity" type="number"
                  value={String(activeModule.quantity_multiplier ?? 1)}
                  onChange={(v) => onModuleChange({
                    ...activeModule, quantity_multiplier: Math.max(1, Math.round(finiteNumber(v))),
                  })} />
              </FieldRow>
              <Field label="Comment" value={activeModule.comment ?? ''}
                onChange={(v) => onModuleChange({ ...activeModule, comment: v })} />
              <FieldRow>
                <Field label="Roof height above ground" unit="m" type="number"
                  value={millimetresToMetres(activeModule.roof_height_above_ground?.millimetres)}
                  onChange={(v) => onModuleChange({
                    ...activeModule,
                    roof_height_above_ground: { millimetres: metresToMillimetres(v), origin: 'VALUE_ORIGIN_INPUT' },
                  })} />
                <Field label="Roof slope" unit="°" type="number"
                  value={String(activeModule.roof_slope?.degrees ?? 0)}
                  onChange={(v) => onModuleChange({
                    ...activeModule,
                    roof_slope: { degrees: finiteNumber(v), origin: 'VALUE_ORIGIN_INPUT' },
                  })} />
              </FieldRow>
              <button type="button" className="toolbar-button" onClick={onEditGeometry}>
                Edit Roof Geometry
              </button>
            </SectionCard>

            <SectionProfileInputs module={activeModule} onModuleChange={onModuleChange} />

            <SectionCard title="Internal pressure">
              <SelectField
                label="Method"
                value={activeModule.internal_pressure?.method ?? 'INTERNAL_PRESSURE_METHOD_AUTOMATIC'}
                options={[
                  { value: 'INTERNAL_PRESSURE_METHOD_AUTOMATIC', label: 'Automatic standard cases' },
                  { value: 'INTERNAL_PRESSURE_METHOD_MANUAL', label: 'Manual coefficients' },
                ]}
                help="Automatic mode evaluates the required internal-pressure cases in the backend. Manual mode requires both positive and negative coefficients."
                onChange={(method) => onModuleChange({
                  ...activeModule,
                  internal_pressure: {
                    ...activeModule.internal_pressure,
                    method: method as 'INTERNAL_PRESSURE_METHOD_AUTOMATIC' | 'INTERNAL_PRESSURE_METHOD_MANUAL',
                  },
                })}
              />
              {activeModule.internal_pressure?.method === 'INTERNAL_PRESSURE_METHOD_MANUAL' && (
                <FieldRow>
                  <Field label="Positive coefficient" symbol="c_pi_positive" type="number"
                    value={String(activeModule.internal_pressure?.manual_coefficient_positive?.value ?? '')}
                    onChange={(v) => onModuleChange({
                      ...activeModule,
                      internal_pressure: {
                        ...activeModule.internal_pressure,
                        method: 'INTERNAL_PRESSURE_METHOD_MANUAL',
                        manual_coefficient_positive: quantityInput(v, 'c_pi_positive'),
                      },
                    })} />
                  <Field label="Negative coefficient" symbol="c_pi_negative" type="number"
                    value={String(activeModule.internal_pressure?.manual_coefficient_negative?.value ?? '')}
                    onChange={(v) => onModuleChange({
                      ...activeModule,
                      internal_pressure: {
                        ...activeModule.internal_pressure,
                        method: 'INTERNAL_PRESSURE_METHOD_MANUAL',
                        manual_coefficient_negative: quantityInput(v, 'c_pi_negative'),
                      },
                    })} />
                </FieldRow>
              )}
            </SectionCard>
          </>
        )}

        {tab === 'module' && !activeModule && (
          <div className="empty-hint">
            <p>This project has no roof module.</p>
            <button type="button" className="text-button" onClick={onAddModule}>+ Add Roof 1</button>
          </div>
        )}

        {tab === 'products' && (
          <CatalogueSelection selection={catalogue} onChange={onCatalogueChange} />
        )}

        {tab === 'layout' && activeModule && (
          <ZoneLayoutEditor module={activeModule} onChange={onModuleChange} />
        )}
        {tab === 'layout' && !activeModule && (
          <p className="empty-hint">This project has no roof module.</p>
        )}

        {tab === 'view' && (
          <>
            <SectionCard title="Annotation sizes">
              <p className="field-note">Presentation only. 100% equals the renderer size used before these controls were introduced.</p>
              <FieldRow>
                <Field
                  label="Membrane labels"
                  unit="%"
                  type="number"
                  value={String(Math.round(drawingDisplaySettings.membraneLabelScale * 100))}
                  onChange={(v) => onDrawingDisplaySettingsChange({
                    ...drawingDisplaySettings,
                    membraneLabelScale: clampDrawingLabelScale(finiteNumber(v) / 100),
                  })}
                />
                <Field
                  label="Fixing spacing labels"
                  unit="%"
                  type="number"
                  value={String(Math.round(drawingDisplaySettings.fixingLabelScale * 100))}
                  onChange={(v) => onDrawingDisplaySettingsChange({
                    ...drawingDisplaySettings,
                    fixingLabelScale: clampDrawingLabelScale(finiteNumber(v) / 100),
                  })}
                />
              </FieldRow>
            </SectionCard>
            <SectionCard title="Layers">
              <div className="layer-groups">
                {VIEW_LAYER_GROUPS.map((group) => (
                  <section className="layer-group" key={group.title} aria-label={`${group.title} layers`}>
                    <h4 className="layer-group-title">{group.title}</h4>
                    <div className="layer-list">
                      {group.layers.map((layer) => (
                        <CheckboxField key={layer.id} label={layer.label}
                          checked={layers[layer.id] ?? true}
                          onChange={(checked) => onLayerToggle(layer.id, checked)} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </SectionCard>
          </>
        )}
      </div>
    </Panel>
  );
}

function NearbyBuildingsEditor({ buildings, onChange }: {
  buildings: readonly NearbyBuilding[];
  onChange: (buildings: NearbyBuilding[]) => void;
}) {
  const updateBuilding = (index: number, patch: Partial<NearbyBuilding>) => {
    onChange(buildings.map((building, candidate) => candidate === index ? { ...building, ...patch } : building));
  };

  return (
    <div className="nearby-building-list">
      {buildings.map((building, index) => (
        <div className="nearby-building-card" key={building.id ?? index}>
          <div className="nearby-building-heading">
            <strong>Building {index + 1}</strong>
            <button type="button" className="text-button" onClick={() => onChange(buildings.filter((_, candidate) => candidate !== index))}>
              Remove
            </button>
          </div>
          <FieldRow>
            <Field label="Height" unit="m" type="number"
              value={millimetresToMetres(building.height?.millimetres)}
              onChange={(v) => updateBuilding(index, { height: inputLengthMetres(v) })} />
            <Field label="Average height" unit="m" type="number"
              value={millimetresToMetres(building.average_height?.millimetres)}
              onChange={(v) => updateBuilding(index, { average_height: inputLengthMetres(v) })} />
          </FieldRow>
          <FieldRow>
            <Field label="Length" unit="m" type="number"
              value={millimetresToMetres(building.length?.millimetres)}
              onChange={(v) => updateBuilding(index, { length: inputLengthMetres(v) })} />
            <Field label="Distance" unit="m" type="number"
              value={millimetresToMetres(building.distance?.millimetres)}
              onChange={(v) => updateBuilding(index, { distance: inputLengthMetres(v) })} />
          </FieldRow>
          <Field label="Direction" unit="°" type="number"
            value={String(building.direction?.degrees ?? 0)}
            onChange={(v) => updateBuilding(index, {
              direction: { degrees: finiteNumber(v), origin: 'VALUE_ORIGIN_INPUT' },
            })} />
        </div>
      ))}
      <button type="button" className="text-button" onClick={() => onChange([...buildings, createNearbyBuilding(buildings.length + 1)])}>
        Add nearby building
      </button>
    </div>
  );
}

function createNearbyBuilding(index: number): NearbyBuilding {
  return {
    id: `nearby-building-${index}`,
    height: { millimetres: 0, origin: 'VALUE_ORIGIN_INPUT' },
    average_height: { millimetres: 0, origin: 'VALUE_ORIGIN_INPUT' },
    length: { millimetres: 0, origin: 'VALUE_ORIGIN_INPUT' },
    distance: { millimetres: 0, origin: 'VALUE_ORIGIN_INPUT' },
    direction: { degrees: 0, origin: 'VALUE_ORIGIN_INPUT' },
  };
}

function compactDesignMethodLabel(method: DesignMethod): string {
  const label = method.display_name ?? method.id ?? '';
  if (/CEN\/TS\s*17659/i.test(label) && /EN\s*1991-1-4/i.test(label)) {
    return 'CEN/TS 17659 + EN 1991-1-4';
  }
  return label
    .replace(/\s*\(development\)\s*/gi, ' ')
    .replace(/\s+development-v?\d*\s*$/i, '')
    .trim();
}

function inputLengthMetres(value: string) {
  return { millimetres: metresToMillimetres(value), origin: 'VALUE_ORIGIN_INPUT' as const };
}

function quantityInput(value: string, symbol: string) {
  return {
    value: finiteNumber(value),
    symbol,
    display_precision: 2,
    origin: 'VALUE_ORIGIN_INPUT' as const,
  };
}

function manualWindQuantity(value: number) {
  return {
    value,
    symbol: 'v_b,0',
    unit: 'm/s',
    display_precision: 2,
    origin: 'VALUE_ORIGIN_MANUAL_OVERRIDE' as const,
  };
}

function finiteNumber(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function millimetresToMetres(mm: number | undefined): string {
  if (mm === undefined) return '0';
  return String(mm / 1000);
}

function metresToMillimetres(value: string): number {
  return finiteNumber(value) * 1000;
}
